/* src/views/PlatformMap.tsx — /map route.
 *
 * Plain-English (bilingual) sitemap of every reachable URL in the
 * platform. Reached via the small footer link rendered globally in
 * App.tsx — discoverable, low-noise, doesn't compete with the daily
 * dashboards.
 *
 * Grouped by purpose so the page reads like a directory rather than a
 * dump of routes. Each entry: path · short description.
 *
 * Auth notes the user actually cares about are bolded inline (not in a
 * separate column) — keeps the page scannable at a glance.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useViewer } from "../lib/data/viewer";
import { useLang } from "../lib/lang";
// Phase B1 — layout primitives.
import Page from "../components/ui/Page";

interface MapEntry {
  /** The actual href. For routes with :params, use a sensible default. */
  to: string;
  /** What the user sees as the clickable link text. */
  label: string;
  /** Short description, localised. */
  desc: { it: string; en: string };
  /** Optional badge — e.g. "admin only", "DEV only". Localised. */
  badge?: { it: string; en: string };
  /** If true, render as plain text (no Link) — for params like :userId
      where the user can't tap-through without a specific slug. */
  external?: boolean;
}

interface MapSection {
  title: { it: string; en: string };
  intro?: { it: string; en: string };
  entries: MapEntry[];
}

const SECTIONS: MapSection[] = [
  {
    title: { it: "Uso quotidiano", en: "Daily use" },
    intro: {
      it: "Le viste che dovresti tenere aperte ogni giorno.",
      en: "The views you should keep open day-to-day.",
    },
    entries: [
      {
        to: "/forecast/italy",
        label: "/forecast/italy",
        desc: {
          it: "Dashboard principale Italia — KPI, grafici, scenari, deal HubSpot.",
          en: "Main Italy dashboard — KPIs, charts, scenarios, HubSpot deals.",
        },
      },
      {
        to: "/me",
        label: "/me",
        desc: {
          it: "Il tuo profilo — fa redirect a /u/<tuo-slug>. Vedi la tua fetta di forecast.",
          en: "Your profile — redirects to /u/<your-slug>. See your slice of the forecast.",
        },
      },
    ],
  },
  {
    title: { it: "Profili utente", en: "User profiles" },
    intro: {
      it: "Una pagina per ogni persona del team. AE vedono i propri numeri; MID/EXEC vedono i mercati che coprono.",
      en: "One page per team member. AEs see their own numbers; MID/EXEC see the markets they cover.",
    },
    entries: [
      {
        to: "/u/marco",
        label: "/u/:userId",
        desc: {
          it: "Profilo + previsioni personali + deal. Sostituisci :userId con un HubSpot owner ID, email local-part, o User.id (u_<base36>).",
          en: "Profile + personal forecast + deals. Replace :userId with a HubSpot owner ID, email local-part, or User.id (u_<base36>).",
        },
        external: true,
      },
    ],
  },
  {
    title: { it: "Mercati", en: "Markets" },
    intro: {
      it: "Una dashboard per paese. Oggi solo l'Italia è completa; altri paesi mostrano un placeholder.",
      en: "One dashboard per country. Today only Italy is wired up; other countries show a placeholder.",
    },
    entries: [
      {
        to: "/market/italy",
        label: "/market/italy",
        desc: {
          it: "Dashboard Italia (alias di /forecast/italy con configurazione di mercato esplicita).",
          en: "Italy dashboard (alias of /forecast/italy with explicit market config).",
        },
      },
      {
        to: "/market/germany",
        label: "/market/:marketId",
        desc: {
          it: "Placeholder per mercati non ancora collegati. Sostituisci :marketId con il paese.",
          en: "Placeholder for markets not yet wired up. Replace :marketId with the country.",
        },
        external: true,
      },
    ],
  },
  {
    title: { it: "Amministrazione", en: "Administration" },
    intro: {
      it: "Lettura aperta a chi ha la view-password. Le scritture richiedono admin + edit-password.",
      en: "Read-open to anyone past the view-password gate. Writes require admin + edit-password.",
    },
    entries: [
      {
        to: "/admin/users",
        label: "/admin/users",
        desc: {
          it: "Crea/modifica/disattiva utenti. Assegna livello, mercati, owner HubSpot. Watch a un altro utente per debugging.",
          en: "Create/edit/deactivate users. Assign level, markets, HubSpot owner. Watch another user for debugging.",
        },
        badge: { it: "scrittura: solo admin", en: "writes: admin only" },
      },
      {
        to: "/admin/audit",
        label: "/admin/audit",
        desc: {
          it: "Registro append-only di ogni modifica (stato + utenti). Hash before/after, ambito, operatore.",
          en: "Append-only log of every mutation (state + users). Before/after hash, scope, operator.",
        },
        badge: { it: "lettura aperta", en: "open read" },
      },
    ],
  },
];

const DEV_SECTION: MapSection = {
  title: { it: "Sviluppo", en: "Development" },
  intro: {
    it: "Solo in dev build (tree-shaken in produzione).",
    en: "DEV build only (tree-shaken out of production).",
  },
  entries: [
    {
      to: "/__lab",
      label: "/__lab",
      desc: {
        it: "Showcase di ogni primitive: button, badge, card, modal, drawer, toast, swatch, ecc. Stato matrix forzato per QA.",
        en: "Showcase of every primitive: button, badge, card, modal, drawer, toast, swatch, etc. Forced-state matrix for QA.",
      },
      badge: { it: "DEV only", en: "DEV only" },
    },
  ],
};

export default function PlatformMap() {
  const { viewer } = useViewer();
  const lang = useLang();
  // Lab only exists in DEV bundles — include the section conditionally.
  const sections = (import.meta as any).env?.DEV
    ? [...SECTIONS, DEV_SECTION]
    : SECTIONS;

  return (
    <Page>
      {/* Phase A2: title + ViewerChip moved to the global AppTopbar. */}
      <p
        style={{
          marginTop: 0,
          marginBottom: "var(--space-5)",
          color: "var(--text-muted)",
          fontSize: "var(--fs-body)",
        }}
      >
        {lang === "it"
          ? "Ogni URL raggiungibile della piattaforma, raggruppato per scopo. Clicca per aprire."
          : "Every reachable URL in the platform, grouped by purpose. Click to open."}
      </p>

      {sections.map((section, i) => (
        <section
          key={i}
          style={{
            marginBottom: "var(--space-6)",
            paddingBottom: "var(--space-5)",
            borderBottom: i < sections.length - 1 ? "1px solid var(--border)" : "none",
          }}
        >
          <h2
            style={{
              margin: 0,
              marginBottom: "var(--space-2)",
              fontSize: "var(--fs-h3)",
            }}
          >
            {section.title[lang]}
          </h2>
          {section.intro && (
            <p
              style={{
                margin: 0,
                marginBottom: "var(--space-3)",
                color: "var(--text-muted)",
                fontSize: "var(--fs-body-sm)",
              }}
            >
              {section.intro[lang]}
            </p>
          )}
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-2)",
            }}
          >
            {section.entries.map((entry, j) => (
              <li
                key={j}
                style={{
                  display: "grid",
                  gridTemplateColumns: "260px 1fr",
                  gap: "var(--space-3)",
                  padding: "var(--space-2) 0",
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "var(--fs-body-sm)",
                    display: "flex",
                    alignItems: "baseline",
                    gap: "var(--space-2)",
                    flexWrap: "wrap",
                  }}
                >
                  {entry.external ? (
                    <span style={{ color: "var(--text-muted)" }}>{entry.label}</span>
                  ) : (
                    <Link
                      to={entry.to}
                      style={{
                        color: "var(--accent)",
                        textDecoration: "none",
                        fontWeight: 500,
                      }}
                    >
                      {entry.label}
                    </Link>
                  )}
                  {entry.badge && (
                    <span
                      style={{
                        fontSize: "var(--fs-caption)",
                        padding: "0 var(--space-1)",
                        borderRadius: "var(--radius-sm)",
                        background: "var(--bg-2)",
                        color: "var(--text-muted)",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        fontFamily: "var(--font-sans)",
                        fontWeight: 600,
                      }}
                    >
                      {entry.badge[lang]}
                    </span>
                  )}
                </span>
                <span style={{ fontSize: "var(--fs-body-sm)" }}>
                  {entry.desc[lang]}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}

      <p
        style={{
          marginTop: "var(--space-6)",
          color: "var(--text-muted)",
          fontSize: "var(--fs-body-sm)",
        }}
      >
        {viewer
          ? (lang === "it"
              ? <>Connesso come <b>{viewer.name}</b>. Cambia utente dal chip in alto.</>
              : <>Signed in as <b>{viewer.name}</b>. Switch user via the chip above.</>)
          : (lang === "it"
              ? "Non sei ancora connesso — passa per /forecast/italy per inserire la password e scegliere un utente."
              : "Not signed in yet — visit /forecast/italy first to enter the password and pick a user.")}
      </p>
    </Page>
  );
}
